import {useEffect, useRef, useState} from 'react';

import {PIN_STATUS} from '/component/base/PinInput/PinInput.constant';
import {LOGIN_TYPE} from '/enum/biometrics.enum';
import {EVENT_NAMES, LOGIN_ERROR_STATE, LOGIN_STATUS} from '/enum/tealium.enum';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

// Define props interface
interface UseEnterPinInputProps {
  enterTitle: string;
  errorTitle: string;
  checkPinCode: (pin: string) => Promise<boolean>;
  onSubmit: (pin: string) => void;
  lastPaidRank: string;
}

// Define return type
interface UseEnterPinInputReturn {
  onInputComplete: (pin: string) => void;
  onInputPin: (currentPin: string) => Promise<string>;
  getText: () => string;
  pinStatus: string;
}

export const useEnterPinInput = ({
  enterTitle,
  errorTitle,
  checkPinCode,
  onSubmit,
  lastPaidRank,
  onSuccess,
}: UseEnterPinInputProps): UseEnterPinInputReturn => {
  const [pinStatus, setPinStatus] = useState<string>(PIN_STATUS.initial);
  const prevPinStatus = useRef<string>(PIN_STATUS.initial);

  const getText = (): string => {
    if (
      pinStatus === PIN_STATUS.error ||
      (pinStatus === PIN_STATUS.enter &&
        prevPinStatus.current === PIN_STATUS.error)
    ) {
      return errorTitle;
    }
    return enterTitle;
  };

  useEffect(() => {
    prevPinStatus.current = pinStatus;
  }, [pinStatus]);

  const onInputPin = async (currentPin: string): Promise<string> => {
    let currentStatus = pinStatus;

    const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
      eventName: EVENT_NAMES.loginClick,
    });

    if (await checkPinCode(currentPin)) {
      currentStatus = PIN_STATUS.enter;
    } else {
      currentStatus = PIN_STATUS.error;
      sendEvent({
        eventName: 'login',
        success: LOGIN_STATUS.fail,
        error_state: LOGIN_ERROR_STATE.true,
        login_type: LOGIN_TYPE.pin,
        lastPaidRank: lastPaidRank,
      });
    }
    setPinStatus(currentStatus);
    return currentStatus;
  };

  const onInputComplete = (pin: string): void => {
    onSubmit(pin);
    if (onSuccess) {
      onSuccess(); // triggers PinModal to show success modal immediately
    }
  };

  return {
    onInputComplete,
    onInputPin,
    getText,
    pinStatus,
  };
};
